import { ReactNode, useMemo } from 'react';
import { CheckCircle2, FilePlus, Loader2, Trash2, XCircle } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { cn } from '@/frontend/lib/utils';
import { FixtureResult, ReportBundleFiles } from '@/reports/bundles/types';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { IconButton } from '../studioChrome';
import { FileBadge, FileKind, SidebarHeader, baseName, fileKind } from './designerChrome';

export type NewFileKind = 'fixture' | 'rules' | 'module' | 'component' | 'other';
const NEW_FILES: Array<{ kind: NewFileKind; label: string }> = [
  { kind: 'fixture', label: 'designerNewFixture' },
  { kind: 'rules', label: 'designerNewRules' },
  { kind: 'module', label: 'designerNewModule' },
  { kind: 'component', label: 'designerNewComponent' },
  { kind: 'other', label: 'designerNewOther' },
];

const GROUP_OF: Record<FileKind, string> = {
  ts: 'report', tsx: 'report', other: 'report', json: 'report',
  schema: 'schema', schemaRef: 'schema',
  fixture: 'fixtures',
  test: 'tests',
  generated: 'generated',
};
/** the schema's files follow the schema, not the report's versions, and the server writes the generated one: neither is marked changed */
const UNVERSIONED: FileKind[] = ['schema', 'schemaRef', 'generated'];

interface Group {
  key: string;
  title: string;
  meta?: string;
  paths: string[];
}

/**
 * The files, grouped as an author thinks of them — the report's code, the
 * schema it reads, its fixtures, its tests, what the server writes — each with
 * what wants attention: unsaved (a dot), changed since the version published
 * (M), and how a fixture came out at the last save.
 */
export function Explorer({ paths, active, working, saved, published, changedSincePublished, results, schemaMeta, undeletable, onOpen, onNew, onDelete, extra }: {
  paths: string[];
  active: string;
  working: ReportBundleFiles;
  /** the draft as last saved: a file that differs is unsaved */
  saved: ReportBundleFiles;
  /** the latest published version's files, once read; null before, or when there is none */
  published: ReportBundleFiles | null;
  /** whether a file changed since the version published, where comparing its text would not say: a schema's own page */
  changedSincePublished?: (path: string) => boolean;
  results: Array<FixtureResult & { pending?: boolean }>;
  /** what the Schema group's heading says: the schema's title, and draft or which version */
  schemaMeta?: string;
  /** files the bundle cannot be without */
  undeletable: string[];
  onOpen: (path: string) => void;
  onNew?: (kind: NewFileKind) => void;
  onDelete?: (path: string) => void;
  /** more under the files: a schema's Read by */
  extra?: ReactNode;
}) {
  const groups = useMemo<Group[]>(() => {
    const by = new Map<string, string[]>();
    for (const path of paths) {
      const group = GROUP_OF[fileKind(path)];
      by.set(group, [...(by.get(group) ?? []), path]);
    }
    const fixtures = by.get('fixtures') ?? [];
    const all: Group[] = [
      { key: 'report', title: I18n.get('designerGroupReport'), paths: by.get('report') ?? [] },
      { key: 'schema', title: I18n.get('designerGroupSchema'), meta: schemaMeta, paths: by.get('schema') ?? [] },
      { key: 'fixtures', title: I18n.get('designerGroupFixtures'), meta: String(fixtures.length), paths: fixtures },
      { key: 'tests', title: I18n.get('designerGroupTests'), paths: by.get('tests') ?? [] },
      { key: 'generated', title: I18n.get('designerGroupGenerated'), meta: I18n.get('designerReadOnly'), paths: by.get('generated') ?? [] },
    ];
    return all.filter((g) => g.paths.length > 0 || (g.key === 'fixtures' && !!onNew));
  }, [paths, schemaMeta, onNew]);

  return (
    <section className="flex h-full flex-col bg-muted/30" aria-label={I18n.get('reportBundleFiles')}>
      <SidebarHeader title={I18n.get('reportBundleFiles')}>
        {onNew && (
          <DropdownMenu>
            <DropdownMenuTrigger render={<IconButton label={I18n.get('reportBundleAddFile')} size="icon-xs" />}><FilePlus /></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {NEW_FILES.map((item) => <DropdownMenuItem key={item.kind} onClick={() => onNew(item.kind)}>{I18n.get(item.label)}</DropdownMenuItem>)}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </SidebarHeader>
      <div className="min-h-0 flex-1 overflow-y-auto pb-2">
        {groups.map((group) => (
          <section key={group.key} aria-label={group.title}>
            <div className="flex items-center justify-between gap-2 px-3 pt-3 pb-1 text-[0.65rem] font-semibold tracking-wide text-muted-foreground uppercase">
              <span>{group.title}</span>
              {group.meta && <span className="truncate font-normal tracking-normal normal-case">{group.meta}</span>}
            </div>
            <ul>
              {group.paths.map((path) => {
                const kind = fileKind(path);
                return (
                  <li key={path} className={cn('group/file flex items-center pr-1', path === active ? 'bg-sky-100/70' : 'hover:bg-muted')}>
                    <button type="button" onClick={() => onOpen(path)} title={path} aria-current={path === active ? 'true' : undefined}
                      className={cn('flex min-w-0 flex-1 items-center gap-2 py-1 pr-1 pl-4 text-left text-[0.8rem]', kind === 'generated' && 'text-muted-foreground italic')}>
                      <FileBadge path={path} />
                      <span className="truncate">{baseName(path)}</span>
                      <span className="ml-auto flex shrink-0 items-center gap-1.5">
                        {working[path] !== saved[path] && <span role="img" aria-label={I18n.get('reportBundleUnsaved')} className="size-1.5 rounded-full bg-foreground/70" />}
                        {(changedSincePublished ? changedSincePublished(path) : !!published && !UNVERSIONED.includes(kind) && working[path] !== published[path]) && (
                          <span title={I18n.get('designerChangedSincePublished')} aria-label={I18n.get('designerChangedSincePublished')} className="text-[0.65rem] font-semibold text-amber-700">M</span>
                        )}
                        <FixtureMark result={results.find((r) => r.file === path)} />
                      </span>
                    </button>
                    {onDelete && !undeletable.includes(path) && kind !== 'generated' && (
                      <IconButton label={`${I18n.get('reportBundleDeleteFile')} ${path}`} size="icon-xs" className="size-5 opacity-[0] group-hover/file:opacity-100 focus-visible:opacity-100 hover:text-destructive" onClick={() => onDelete(path)}>
                        <Trash2 />
                      </IconButton>
                    )}
                  </li>
                );
              })}
              {group.paths.length === 0 && <li className="px-5 py-1 text-xs text-muted-foreground">{I18n.get('designerNoFixtures')}</li>}
            </ul>
          </section>
        ))}
        {extra}
      </div>
    </section>
  );
}

/** How a fixture came out at the last save; still checking what it prints. */
export function FixtureMark({ result }: { result: (FixtureResult & { pending?: boolean }) | undefined }) {
  if (!result) return null;
  if (result.ok && result.pending) return <Loader2 role="img" aria-label={I18n.get('checkPending')} className="size-3.5 animate-spin text-muted-foreground" />;
  return result.ok
    ? <CheckCircle2 role="img" aria-label={I18n.get('designerFixtureHolds')} className="size-3.5 text-emerald-600" />
    : <XCircle role="img" aria-label={I18n.get('designerFixtureFails')} className="size-3.5 text-red-600" />;
}
